import { useCurrentUser } from '@/affiliate-dashboard/context/current-user-context';
import {
  useGetCommissionsMetricsQuery,
  useGetUserMetricsQuery,
} from '@/affiliate-dashboard/graphql';
import { Label } from '@/components/base/label';
import { PageHeading } from '@/components/base/page-heading';
import Section from '@/components/base/section';
import { cn } from '@/utils';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faDollar,
  faHandHoldingDollar,
  faHandshake,
  faSackDollar,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';
import { ManageAffiliateCode } from './manage-affiliate-code';

function MetricCard({
  icon,
  label,
  value,
  className,
}: {
  icon: IconProp;
  label: string;
  value: string | number;
  className?: string;
}): JSX.Element {
  return (
    <Section variant="secondary" className={cn(className)}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full items-center gap-4">
          <Label className="flex items-center justify-center">
            <FontAwesomeIcon className="h-5 w-6" icon={icon} />
          </Label>
          <span>{label}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-3xl font-bold">{value}</span>
        </div>
      </div>
    </Section>
  );
}

export function Overview(): JSX.Element {
  const intl = useIntl();
  const { t } = useTranslation();
  const data = useCurrentUser();
  const { data: commissionsMetrics } = useGetCommissionsMetricsQuery();
  const { data: metrics } = useGetUserMetricsQuery();
  const userMetrics = metrics?.userMetrics;

  function formatCurrency(value: number): string {
    return intl.formatNumber(value, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  }

  return (
    <div className="flex flex-col gap-5 pb-12">
      <div className="flex w-full items-center justify-between">
        <PageHeading subheading={t('affiliateDashboard.subtitle')}>
          {t('affiliateDashboard.title')}
        </PageHeading>
      </div>
      <div
        tabIndex={-1}
        className={cn(
          'grid auto-rows-min grid-cols-1 gap-3 lg:grid-cols-6 lg:grid-rows-3',
          {
            'pointer-events-none opacity-30 blur-sm': !data.me.emailVerifiedAt,
          },
        )}
      >
        <MetricCard
          label={t('affiliateDashboard.monthRevenueCardTitle')}
          value={formatCurrency(userMetrics?.currentMonthRevenue ?? 0)}
          icon={faDollar}
          className="col-span-1 row-span-1 w-full lg:col-span-2"
        />
        <MetricCard
          label={t('affiliateDashboard.monthCommissionsCardTitle')}
          value={formatCurrency(
            commissionsMetrics?.commissionsMetrics
              ?.totalCommissionsAmountThisMonth ?? 0,
          )}
          icon={faSackDollar}
          className="col-span-1 row-span-1 w-full lg:col-span-2"
        />
        <MetricCard
          label={t('affiliateDashboard.unpaidCommissionsCardTitle')}
          value={formatCurrency(userMetrics?.unpaidCommissionsAmount ?? 0)}
          icon={faHandHoldingDollar}
          className="col-span-1 row-span-1 w-full lg:col-span-2"
        />
        <div className="col-span-1 row-span-1 w-full lg:col-span-4 lg:row-span-2">
          <ManageAffiliateCode refcode={data.me?.refcode ?? ''} />
        </div>

        <MetricCard
          label={t('affiliateDashboard.subAffiliatesCardTitle')}
          value={userMetrics?.subAffiliatesNumber ?? 0}
          icon={faHandshake}
          className="col-span-1 row-start-3 row-end-3 lg:col-span-2 lg:col-start-5 lg:row-start-2 lg:row-end-3"
        />
        <MetricCard
          label={t('affiliateDashboard.customersCardTitle')}
          value={userMetrics?.customersNumber ?? 0}
          icon={faUsers}
          className="col-span-1 row-start-4 row-end-4 lg:col-span-2 lg:col-start-5 lg:row-start-3 lg:row-end-4"
        />
      </div>
    </div>
  );
}
